/* eslint-disable no-restricted-syntax */
/* eslint-disable no-await-in-loop */
/* eslint-disable no-nested-ternary */
/* eslint-disable no-restricted-globals */
/* eslint-disable no-case-declarations */

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import EditIcon from '@material-ui/icons/Edit';
import { IconButton, Switch, Tooltip } from '@material-ui/core';
import Immutable, { Map as ImmutableMap } from 'immutable';
import lightStyles from './portalStylesLight.module.css';
import darkStyles from './portalStylesDark.module.css';
import { IvrLists } from '../App';
import IVRPromptModal from './components/IVRPromptModal';
import Notes from './Notes';

export interface VMOnly {
  firstName: string;
  lastName: string;
  extensionNumber: string;
  id: string;
}

interface Props {
  token: string;
  historicalQueues: ImmutableMap<string, any>;
  setHistoricalQueues: React.SetStateAction<any>;
  presenceTables: any[];
  vmOnlyExtensions: VMOnly[];
  ivrList: IvrLists[];
  lightMode: boolean;
  showCallback: boolean;
}

export interface Queue {
  PartitionKey: string;
  SortKey: string;
  acw: {
    duration: string;
    label: string;
  };
  editableMemberStatus: boolean;
  extensionNumber: string;
  id: string;
  isCallback: string;
  ivrId: string;
  missingCallThreshold: string;
  name: string;
  priority: string;
  promptActive: boolean;
  status: string;
  uri: string;
  text?: string;
}

const MAX_DURATION = 60 * 60 * 24; // 1 Day Max
const MAX_THRESHOLD = 1000; // Maximum threshold value
const MIN_THRESHOLD = 1; // Minimum threshold value

export default function QueuesPortal({
  token,
  historicalQueues,
  setHistoricalQueues,
  presenceTables,
  vmOnlyExtensions,
  ivrList,
  lightMode,
  showCallback,
}: Props) {
  const [rowData, setRowData] = useState<Queue[]>(Array.from(historicalQueues.values()));
  const [modal, setModal] = useState<Queue | undefined>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [wrapLabels] = useState(() => [
    'Do not disturb',
    ...presenceTables[0].items.filter((item: any) => item.category === 'dnd').map((item: any) => item.status),
  ]);
  const gridRef = useRef<AgGridReact>(null);

  const styles = lightMode ? lightStyles : darkStyles;

  useEffect(() => {
    setRowData(Array.from(historicalQueues.values()));
  }, [historicalQueues]);

  // Validate duration input
  const validateDuration = (value: any): number | null => {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    
    const numValue = Number(value);
    if (isNaN(numValue) || numValue < 0 || numValue > MAX_DURATION || !Number.isInteger(numValue)) {
      return null;
    }
    
    return numValue;
  };

  // Validate threshold input
  const validateThreshold = (value: any): number | null => {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    
    const numValue = Number(value);
    if (isNaN(numValue) || numValue < MIN_THRESHOLD || numValue > MAX_THRESHOLD || !Number.isInteger(numValue)) {
      return null;
    }
    
    return numValue;
  };

  // Handle modal open/close
  const handleModalOpen = useCallback((queue: Queue) => {
    setModal(queue);
    setIsModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setModal(undefined);
  }, []);

  // Handle prompt update
  const handlePromptUpdate = useCallback(
    (queueId: string, newPrompt: string) => {
      setHistoricalQueues((oldHistoricalQueues: ImmutableMap<string, any>) => {
        const oldItem = oldHistoricalQueues.get(queueId);
        if (!oldItem) return oldHistoricalQueues;

        const updatedItem = {
          ...oldItem,
          text: newPrompt,
        };
        return oldHistoricalQueues.set(queueId, updatedItem);
      });
    },
    [setHistoricalQueues],
  );

  // Edit Wrap
  const onCheckboxChange = useCallback(
    async (id: string, checked: boolean) => {
      try {
        setError(null);
        const body = checked ? { acw: true, duration: 0, label: 'Do not disturb' } : { acw: false, label: '' };
        const res = await fetch(`${process.env.REACT_APP_API_URL}/queues/${id}`, {
          body: JSON.stringify(body),
          method: 'PUT',
          headers: {
            authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) {
          throw new Error(`Failed to update wrap up setting: ${res.statusText}`);
        }

        setHistoricalQueues((oldHistoricalQueues: ImmutableMap<string, any>) => {
          const oldItem = oldHistoricalQueues.get(id);
          if (!oldItem) return oldHistoricalQueues;

          // Create updated item
          const updatedItem = {
            ...oldItem,
            acw: checked
              ? {
                  ...oldItem.acw,
                  duration: 0,
                  label: 'Do not disturb',
                }
              : undefined,
          };

          // Return new ImmutableMap with the updated item
          return oldHistoricalQueues.set(id, updatedItem);
        });

        // Update grid data
        const gridApi = gridRef.current?.api;
        const rowNode = gridApi?.getRowNode(id);
        if (rowNode) {
          rowNode.setDataValue('acw', checked ? { duration: 0, label: 'Do not disturb' } : null);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update wrap up setting');
        console.error('Error updating wrap up setting:', err);
      }
    },
    [token, setHistoricalQueues],
  );

  // Column value change handler
  // Crud for duration and threshold
  const onCellValueChanged = useCallback(async (params: any) => {
    try {
      setError(null);
      const columnHeaderName = params.colDef.field;
      let res;
      switch (columnHeaderName) {
        // Wrap up duration
        case 'acw.duration':
          const validatedDuration = validateDuration(params.newValue);
          if (validatedDuration === null && params.newValue !== null && params.newValue !== '') {
            setError('Duration must be a non-negative integer between 0 and 86400 seconds (24 hours)');
            return;
          }
          
          setHistoricalQueues((oldHistoricalQueues: ImmutableMap<string, any>) => {
            const oldItem = oldHistoricalQueues.get(params.data.id);
            if (!oldItem) return oldHistoricalQueues;

            // Create updated item
            const updatedItem = {
              ...oldItem,
              acw: {
                ...oldItem.acw,
                duration: validatedDuration,
              },
            };
            // Return new ImmutableMap with the updated item
            return oldHistoricalQueues.set(params.data.id, updatedItem);
          });
          res = await fetch(`${process.env.REACT_APP_API_URL}/queues/${params.data.id}`, {
            method: 'PUT',
            headers: {
              authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              acw: true,
              duration: `${validatedDuration}`,
              label: `${params.data.acw.label}`,
            }),
          });
          break;
        // Queue Call Threshold
        case 'missingCallThreshold':
          const validatedThreshold = validateThreshold(params.newValue);
          if (validatedThreshold === null && params.newValue !== null && params.newValue !== '') {
            setError(`Threshold must be an integer between ${MIN_THRESHOLD} and ${MAX_THRESHOLD}`);
            return;
          }
          
          setHistoricalQueues((oldHistoricalQueues: ImmutableMap<string, any>) => {
            const oldItem = oldHistoricalQueues.get(params.data.id);
            if (!oldItem) return oldHistoricalQueues;

            // Create updated item
            const updatedItem = {
              ...oldItem,
              missingCallThreshold: validatedThreshold,
            };
            // Return new ImmutableMap with the updated item
            return oldHistoricalQueues.set(params.data.id, updatedItem);
          });
          res = await fetch(`${process.env.REACT_APP_API_URL}/queues/threshold/${params.data.id}`, {
            method: 'PUT',
            headers: {
              authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              missingCallThreshold: `${validatedThreshold}`,
            }),
          });
          break;
        default:
          break;
      }
      
      if (res && !res.ok) {
        throw new Error(`Failed to update ${columnHeaderName}: ${res.statusText}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update queue setting');
      console.error('Error updating queue setting:', err);
    }
  }, [token, setHistoricalQueues]);

  // ACW label crud
  const onLabelChange = useCallback(async (params: any, newValue) => {
    try {
      setError(null);
      setHistoricalQueues((oldHistoricalQueues: ImmutableMap<string, any>) => {
        const oldItem = oldHistoricalQueues.get(params.data.id);
        if (!oldItem) return oldHistoricalQueues;

        // Create updated item
        const updatedItem = {
          ...oldItem,
          acw: {
            ...oldItem.acw,
            label: newValue,
          },
        };
        // Return new ImmutableMap with the updated item
        console.log(updatedItem);
        return oldHistoricalQueues.set(params.data.id, updatedItem);
      });
      const res = await fetch(`${process.env.REACT_APP_API_URL}/queues/${params.data.id}`, {
        method: 'PUT',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          acw: true,
          duration: `${params.data.acw.duration}`,
          label: `${newValue}`,
        }),
      });
      
      if (!res.ok) {
        throw new Error(`Failed to update ACW label: ${res.statusText}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update ACW label');
      console.error('Error updating ACW label:', err);
    }
  }, [token, setHistoricalQueues]);

  // Callback crud
  const onCallbackChange = useCallback(async (params, newValue) => {
    try {
      setError(null);
      setHistoricalQueues((oldHistoricalQueues: ImmutableMap<string, any>) => {
        const oldItem = oldHistoricalQueues.get(params.data.id);
        if (!oldItem) return oldHistoricalQueues;

        // Create updated item
        const updatedItem = {
          ...oldItem,
          isCallback: newValue,
        };
        // Return new ImmutableMap with the updated item
        console.log(updatedItem);
        return oldHistoricalQueues.set(params.data.id, updatedItem);
      });
      const res = await fetch(`${process.env.REACT_APP_API_URL}/queues/${params.data.id}`, {
        method: 'PUT',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          isCallback: newValue,
        }),
      });
      
      if (!res.ok) {
        throw new Error(`Failed to update callback setting: ${res.statusText}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update callback setting');
      console.error('Error updating callback setting:', err);
    }
  }, [token, setHistoricalQueues]);

  // Ivr change crud
  const onIvrChange = useCallback(
    async (params, newValue) => {
      try {
        setError(null);
        setHistoricalQueues((oldHistoricalQueues: ImmutableMap<string, any>) => {
          const oldItem = oldHistoricalQueues.get(params.data.id);
          if (!oldItem) return oldHistoricalQueues;

          // Create updated item
          const updatedItem = {
            ...oldItem,
            ivrId: newValue,
            promptActive: !!newValue,
          };
          // Return new ImmutableMap with the updated item
          console.log(updatedItem);
          return oldHistoricalQueues.set(params.data.id, updatedItem);
        });

        // Update grid data immediately to prevent cell renderer disappearing
        const gridApi = gridRef.current?.api;
        if (gridApi) {
          const rowNode = gridApi.getRowNode(params.data.id);
          if (rowNode) {
            rowNode.setDataValue('ivrId', newValue);
            rowNode.setDataValue('promptActive', !!newValue);
          }
        }

        if (newValue) {
          const res = await fetch(`${process.env.REACT_APP_API_URL}/prompt/${params.data.id}`, {
            method: 'PUT',
            headers: {
              authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              ivrId: newValue,
              promptActive: !!newValue,
            }),
          });
          
          if (!res.ok) {
            throw new Error(`Failed to update IVR setting: ${res.statusText}`);
          }
        } else {
          const res = await fetch(`${process.env.REACT_APP_API_URL}/prompt/${params.data.id}`, {
            method: 'DELETE',
            headers: {
              authorization: `Bearer ${token}`,
            },
          });
          
          if (!res.ok) {
            throw new Error(`Failed to remove IVR setting: ${res.statusText}`);
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update IVR setting');
        console.error('Error updating IVR setting:', err);
      }
    },
    [token, setHistoricalQueues],
  );

  const onIvrActiveToggle = useCallback(async (params, newValue) => {
    try {
      setError(null);
      console.log(params, newValue);
      setHistoricalQueues((oldHistoricalQueues: ImmutableMap<string, any>) => {
        const oldItem = oldHistoricalQueues.get(params.data.id);
        if (!oldItem) return oldHistoricalQueues;

        // Create updated item
        const updatedItem = {
          ...oldItem,
          promptActive: newValue,
        };
        // Return new ImmutableMap with the updated item
        console.log(updatedItem);
        return oldHistoricalQueues.set(params.data.id, updatedItem);
      });
      const res = await fetch(`${process.env.REACT_APP_API_URL}/prompt/${params.data.id}`, {
        method: 'PUT',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          promptActive: newValue,
        }),
      });
      
      if (!res.ok) {
        throw new Error(`Failed to update IVR active status: ${res.statusText}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update IVR active status');
      console.error('Error updating IVR active status:', err);
    }
  }, [token, setHistoricalQueues]);

  const columnDefs = useMemo(() => {
    const cols: any[] = [
      {
        headerName: 'Name',
        field: 'name',
        editable: false,
        flex: 1,
      },
      {
        headerName: 'Ext',
        field: 'extensionNumber',
        editable: false,
        flex: 1,
      },
      {
        headerName: 'Wrap Up',
        field: 'acw',
        cellRenderer: (params: any) => {
          return (
            <input
              type="checkbox"
              checked={!!params.value}
              onChange={(e) => onCheckboxChange(params.data.id, e.target.checked)}
            />
          );
        },
        cellEditor: 'agCheckboxCellEditor',
        editable: false,
        width: 125,
        suppressSizeToFit: true,
        headerToolTip: "When wrap up is enabled agents in this queue will not receive calls for the duration entered into 'Wrap Up Time'.",
      },
      {
        headerName: 'Wrap Up Time (sec)',
        field: 'acw.duration',
        editable: true,
        flex: 1,
        cellEditor: 'agNumberCellEditor',
        cellEditorParams: {
          min: 0,
          max: MAX_DURATION,
          step: 5,
        },
        valueParser: (params: any) => {
          return validateDuration(params.newValue);
        },
      },
      {
        headerName: 'Acw Label',
        field: 'acw.label',
        editable: false,
        flex: 1,
        cellRenderer: (params: any) => {
          // Only show dropdown if ACW is enabled
          if (!params.data?.acw) {
            return null;
          }

          return (
            <select
              className={styles.dropdown}
              value={params.value || ''}
              onChange={(e) => onLabelChange(params, e.target.value)}
              disabled={!params.data?.acw}
            >
              <option value="">Select a Label</option>
              {wrapLabels.map((label) => (
                <option key={label} value={label}>
                  {label}
                </option>
              ))}
            </select>
          );
        },
      },
      {
        headerName: 'Threshold',
        field: 'missingCallThreshold',
        editable: true,
        flex: 1,
        cellEditor: 'agNumberCellEditor',
        cellEditorParams: {
          min: MIN_THRESHOLD,
          max: MAX_THRESHOLD,
          step: 1,
        },
        valueParser: (params: any) => {
          return validateThreshold(params.newValue);
        },
      },
    ];
    if (showCallback) {
      return cols.concat([
        {
          headerName: 'Callback',
          field: 'isCallback',
          editable: false,
          flex: 1,
          cellRenderer: (params: any) => {
            return (
              <select
                className={styles.dropdown}
                value={params.value || ''}
                onChange={(e) => onCallbackChange(params, e.target.value)}
              >
                <option value="none">Select a Message Only Extension</option>
                {vmOnlyExtensions?.map((vm) => (
                  <option key={vm.id} value={vm.id}>
                    {vm.extensionNumber} - {vm.firstName || ''} {vm.lastName || ''}
                  </option>
                ))}
              </select>
            );
          },
        },
        {
          headerName: 'Greeting IVR',
          field: 'ivrId',
          editable: false,
          width: 200,
          cellRenderer: (params: any) => {
            // Get all currently used IVR IDs (excluding the current queue's IVR)
            const usedIvrIds = Array.from(historicalQueues.values())
              .filter((queue: Queue) => queue.id !== params.data.id && queue.ivrId)
              .map((queue: Queue) => queue.ivrId);

            return (
              <>
                <IconButton
                  style={{ padding: 0, paddingRight: 8 }}
                  onClick={() => {
                    handleModalOpen(params.data as Queue);
                  }}
                  disabled={!params.data.ivrId}
                >
                  <EditIcon
                    fontSize="small"
                    style={{
                      color: (() => {
                        if (lightMode) {
                          return !params.data.ivrId ? '#9e9e9e' : 'black';
                        }
                        return !params.data.ivrId ? '#9e9e9e' : '#0e6bce';
                      })(),
                    }}
                  />
                </IconButton>
                <select
                  className={styles.dropdown}
                  value={params.value || ''}
                  onChange={(e) => onIvrChange(params, e.target.value)}
                >
                  <option value="">Select an IVR</option>

                  {/* For greying out IVRs that are already in use */}
                  {/*{ivrList?.map((ivr) => (
                  <option key={ivr.id} value={ivr.id}>
                    {ivr.extensionNumber} - {ivr.name}
                  </option>
                ))} */}
                  {ivrList?.map((ivr) => {
                    const isUsed = usedIvrIds.includes(ivr.id);
                    const isCurrentSelection = params.value === ivr.id;

                    return (
                      <option
                        key={ivr.id}
                        value={ivr.id}
                        disabled={isUsed && !isCurrentSelection}
                        style={{
                          color: isUsed && !isCurrentSelection ? '#9e9e9e' : 'inherit',
                          fontStyle: isUsed && !isCurrentSelection ? 'italic' : 'normal',
                        }}
                      >
                        {ivr.extensionNumber} - {ivr.name}
                        {isUsed && !isCurrentSelection ? ' (In Use)' : ''}
                      </option>
                    );
                  })}
                </select>
              </>
            );
          },
        },
        {
          headerName: 'IVR Active',
          field: 'promptActive',
          editable: false,
          // flex: 1,
          width: 200,
          cellRenderer: (params: any) => {
            return (
              <Switch
                checked={!!params.data.ivrId && params.data.promptActive}
                disabled={!params.data.ivrId}
                onChange={(e) => onIvrActiveToggle(params, e.target.checked)}
                color="primary"
              />
            );
          },
        },
      ]);
    }
    return cols;
  }, [
    historicalQueues,
    ivrList,
    lightMode,
    wrapLabels,
    vmOnlyExtensions,
    onCheckboxChange,
    onLabelChange,
    onCallbackChange,
    onIvrChange,
    onIvrActiveToggle,
    handleModalOpen,
  ]);

  const defaultColDef = useMemo(
    () => ({
      sortable: false,
      filter: true,
      resizable: true,
      minWidth: 50,
    }),
    [],
  );

  const onGridReady = useCallback((params: any) => {
    const allColumnIds: string[] = [];
    params.columnApi.getAllColumns().forEach((column: any) => {
      allColumnIds.push(column.getId());
    });
    params.columnApi.autoSizeColumns(allColumnIds, false);
  }, []);

  useEffect(() => {
    if (gridRef.current && gridRef.current.columnApi) {
      const allColumnIds: string[] = [];
      gridRef.current.columnApi.getColumns()?.forEach((column: any) => {
        allColumnIds.push(column.getId());
      });
      gridRef.current.columnApi.autoSizeColumns(allColumnIds, false);
    }
  }, [rowData]);

  return (
    <div className={styles.gridContainer}>
      {error && (
        <div className={styles.errorMessage} style={{ 
          color: 'red', 
          padding: '8px', 
          margin: '8px 0', 
          backgroundColor: 'rgba(255, 0, 0, 0.1)', 
          borderRadius: '4px',
          border: '1px solid red'
        }}>
          {error}
        </div>
      )}
      <div className={styles.navbar}>
        <div className={styles.tabButtons}>
          <button type="button" className={`${styles.subTabButton} ${styles.subTabButtonActive}`}>
            Manage Queues
          </button>
        </div>
      </div>

      <div
        className={`ag-theme-alpine${!lightMode ? '-dark' : ''}  ${styles.agThemeAlpineOverrides} ${
          styles.gridWrapper
        }`}
        style={{ flex: 3 }}
      >
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          singleClickEdit
          stopEditingWhenCellsLoseFocus
          animateRows
          suppressCellFocus
          rowSelection="multiple"
          suppressRowClickSelection
          suppressDragLeaveHidesColumns
          headerHeight={40}
          rowHeight={40}
          onGridReady={onGridReady}
          getRowId={(params) => params.data.id}
          onCellValueChanged={onCellValueChanged}
          tooltipShowDelay={500}
        />
      </div>
      <IVRPromptModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        modalSubtitle={`Edit IVR Prompt for ${modal?.name || ''}`}
        queueId={modal?.id || ''}
        queueName={modal?.name || ''}
        queueExtension={modal?.extensionNumber || ''}
        token={token}
        setRowData={handlePromptUpdate}
        lightMode={lightMode}
      />
      <Notes
        lightMode={lightMode}
        notes={`
            This tab allows for queue configurations. 
            Wrap up means whenever an agent ends a queue call, they are put
            into the selected Acw Label state for Wrap Up Time seconds. During
            this state they will not recieve any calls. Threshold defines how
            many calls an agent can miss from that queue before they are signed
            out of it.
            
            Wrap Up Time must be between 0 and ${MAX_DURATION} seconds (24 hours).
            Threshold must be between ${MIN_THRESHOLD} and ${MAX_THRESHOLD}.
          `}
        // documents={[
        //   { name: 'Supporting Document', uri: 'https://google.ca' },
        //   { name: 'Instruction Manual', uri: 'https://yahoo.ca' },
        //   { name: 'Demo', uri: 'https://bing.com' },
        // ]}
      />
    </div>
  );
}
